"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@/module_bindings";
import { useResolvedCurrentOrganization } from "./use-current-organization";

export type SyncBadgeState = "synced" | "publishing" | "error";
export type SyncBadgeStatus = { state: SyncBadgeState; error?: string };

type TsLike = { toDate: () => Date } | undefined;
const ms = (ts: TsLike) => (ts ? ts.toDate().getTime() : 0);

function toStatus(
  entityUpdatedMs: number,
  sync?: { publishedUpdatedAt?: TsLike; status: string; error?: string },
): SyncBadgeStatus {
  if (!sync) return { state: "publishing" };
  if (entityUpdatedMs > ms(sync.publishedUpdatedAt))
    return { state: "publishing" };
  if (sync.status === "error") return { state: "error", error: sync.error };
  return { state: "synced" };
}

// Org chip only: an error status must surface even when unrelated org
// churn (name change, credential save, Stripe detach) bumps org.updatedAt
// past the sync row's publishedUpdatedAt. The ack module deliberately
// declines to advance error rows, so staleness-first would otherwise mask
// a failed config publish behind a permanent "Publishing…" state.
function toOrgStatus(
  entityUpdatedMs: number,
  sync?: { publishedUpdatedAt?: TsLike; status: string; error?: string },
): SyncBadgeStatus {
  if (!sync) return { state: "publishing" };
  if (sync.status === "error") return { state: "error", error: sync.error };
  if (entityUpdatedMs > ms(sync.publishedUpdatedAt))
    return { state: "publishing" };
  return { state: "synced" };
}

export function useOrgPlaybookSync(orgId?: string): {
  syncByPlaybookId: Map<string, SyncBadgeStatus>;
  isReady: boolean;
} {
  const current = useResolvedCurrentOrganization();
  const effectiveOrgId = orgId ?? current.organization?.id;

  const [playbookRows, playbooksReady] = useTable(tables.org_playbooks);
  const [syncRows, syncReady] = useTable(tables.org_playbook_sync);

  const syncByPlaybookId = useMemo(() => {
    const map = new Map<string, SyncBadgeStatus>();
    if (!effectiveOrgId) return map;

    const filteredPlaybooks = playbookRows.filter(
      (row) => row.organizationId === effectiveOrgId,
    );
    const syncById = new Map(
      syncRows
        .filter((row) => row.organizationId === effectiveOrgId)
        .map((row) => [row.playbookId, row]),
    );

    for (const pb of filteredPlaybooks) {
      const entityUpdatedMs = ms(pb.updatedAt);
      const sync = syncById.get(pb.id);
      map.set(pb.id, toStatus(entityUpdatedMs, sync));
    }

    return map;
  }, [playbookRows, syncRows, effectiveOrgId]);

  const isReady = orgId
    ? playbooksReady && syncReady
    : playbooksReady && syncReady && current.isReady;

  return { syncByPlaybookId, isReady };
}

export function useOrgGlaciSync(orgId?: string): {
  status: SyncBadgeStatus | undefined;
  isReady: boolean;
} {
  const current = useResolvedCurrentOrganization();
  const effectiveOrgId = orgId ?? current.organization?.id;

  const [orgRows, orgsReady] = useTable(tables.my_organizations);
  const [syncRows, syncReady] = useTable(tables.org_glaci_sync);

  const status = useMemo(() => {
    if (!effectiveOrgId) return undefined;

    const org = orgRows.find((row) => row.id === effectiveOrgId);
    if (!org) return undefined;

    const sync = syncRows.find((row) => row.organizationId === effectiveOrgId);
    const entityUpdatedMs = ms(org.updatedAt);
    return toOrgStatus(entityUpdatedMs, sync);
  }, [orgRows, syncRows, effectiveOrgId]);

  const isReady = orgId
    ? orgsReady && syncReady
    : orgsReady && syncReady && current.isReady;

  return { status, isReady };
}
